// ═══════════════════════════════════════════════════════════════════
// fieldProps3D.jsx — SHIP 8.4 · THE FURNITURE
// ═══════════════════════════════════════════════════════════════════
// caP'n: "make what needs making, mausoleums, the treasures, everything,
// true 3D battling."
//
// Everything on the Shatterfield that is a THING and not a being:
//   · THE TREASURES  — the nine Treasure Tomes as objects (necklaces,
//     blades, the dagger, gauntlets, boots, the cuirass)
//   · THE CONSTRUCTIONS — the nine Construction Tomes as places (the
//     Khao gardens, the Bhodi Tree, the Anima House, the Stars and
//     Skies, the three D'uhmdumn guardians)
//   · THE RITES — the nine Ritual Tomes as sacred ground (a Seal, two
//     Monoliths, a Mausoleum; tinted Omnis / Nihil / Soulcerer)
//   · THE CELLAR — the Necropolis as a place: a stone trapdoor thrown
//     open in the felt, a green vortex under the pile; and THE DEATH
//     MARK, the skull that rises out of it when a being is ruined
//   · THE EXILE PIT — the rift the 3D board never had (R112), a well of
//     violet nothing beside the graves
//
// THE TRANSLATION LAW (ie-being-models): every prop is built from what
// the card SAYS it does. A Necklace of Congealed Death is fed Anima and
// ruins a being — so its jewel is a clot, dark and red, and it drips.
// The Boots of Wing Walking make a being Intangible — so they hover.
// The D'uhmdumn must be ruined before anything else — so it is a wall
// with a shield. Nothing here is invented past the printed text.
//
// THE RECIPE: no bare boxes except slabs and blades (which honestly are
// slabs); spheres, capsules, cones, tori; one painted stone canvas and
// one painted metal canvas shared by everything; emissive accents, ZERO
// new lights; idle life on one useFrame per prop, sin-driven, fixed
// phases; mulberry32 for every scatter, never Math.random().
// PURE RENDERER: reads a card id, draws a thing. Nothing out.
// ═══════════════════════════════════════════════════════════════════
import { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { canvasTex, mulberry32 } from "./ieBeingModels";
import { CARD } from "./fieldLayout3D";
import { baseId } from "./necroSeal";
import { getMotion, motionRate } from "./strikeLanguage";
// the kick bus — a prop performs when its Tome is cast (SHIP 8.7)
import { beingLife } from "./fieldBeings3D";

// ── THE SURFACES (painted once, cached by key, shared) ────────────
function stoneTex() {
  return canvasTex("sf_prop_stone_v1", 256, 256, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, w, h);
    base.addColorStop(0, "#3a3342"); base.addColorStop(1, "#221c2b");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(4411);
    for (let i = 0; i < 900; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(255,240,220,0.05)" : "rgba(0,0,10,0.12)";
      g.fillRect(R() * w, R() * h, 2 + R() * 6, 1 + R() * 3);
    }
    g.strokeStyle = "rgba(0,0,8,0.35)"; g.lineWidth = 1.2;
    for (let i = 0; i < 9; i++) { g.beginPath(); g.moveTo(R() * w, R() * h); g.lineTo(R() * w, R() * h); g.stroke(); }
  });
}
function metalTex() {
  return canvasTex("sf_prop_metal_v1", 256, 256, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#8a8f9c"); base.addColorStop(0.5, "#5a5f6c"); base.addColorStop(1, "#7c8290");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(919);
    for (let i = 0; i < 400; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.14)";
      g.fillRect(0, R() * h, w, 1 + R() * 2);
    }
  });
}
function leafTex() {
  return canvasTex("sf_prop_leaf_v1", 128, 128, (g, w, h) => {
    g.fillStyle = "#1f4a2c"; g.fillRect(0, 0, w, h);
    const R = mulberry32(77);
    for (let i = 0; i < 260; i++) {
      g.fillStyle = R() > 0.6 ? "rgba(150,230,140,0.18)" : "rgba(0,20,0,0.18)";
      g.beginPath(); g.arc(R() * w, R() * h, 2 + R() * 5, 0, 7); g.fill();
    }
  });
}

// The school tints for the Rites, the Khao violet, the Anima warmth.
const T = {
  omnis: "#e8b84b", nihil: "#b8e8ff", soul: "#a87cff",
  khao: "#7c3aed", khaoHi: "#c9b8ff", anima: "#ff7a6b", animaHi: "#ffd0a0",
  bolt: "#7ad8ff", shadow: "#5b21b6", blood: "#c8203a", gold: "#f0d060", teal: "#7fe8d8",
};
const STONE = { roughness: 0.9, metalness: 0.05 };
const METAL = { roughness: 0.35, metalness: 0.85 };
const GLOW = (color, k = 1) => ({ color, emissive: color, emissiveIntensity: k, toneMapped: false });
const NO_RAY = () => null;

// ── PRIMITIVES ────────────────────────────────────────────────────
// A bead-chain along a hand-tuned curve (the house tail/tentacle/chain).
function Beads({ pts, color, r0 = 0.03, r1 = 0.015, mat = {} }) {
  return pts.map((p, i) => {
    const r = r0 + (r1 - r0) * (i / Math.max(1, pts.length - 1));
    return (
      <mesh key={i} position={p} raycast={NO_RAY}>
        <sphereGeometry args={[r, 8, 6]} />
        <meshStandardMaterial color={color} {...mat} />
      </mesh>
    );
  });
}
// A pulsing emissive — one hook drives one material.
function usePulse(ref, base, amp, rate, phase = 0) {
  useFrame((s) => {
    const m = ref.current && ref.current.material;
    if (m) m.emissiveIntensity = base + Math.sin(s.clock.elapsedTime * rate + phase) * amp;
  });
}

// ═══════════════════════════════════════════════════════════════════
// THE TREASURES
// ═══════════════════════════════════════════════════════════════════
// Drops that FALL — nothing else on the table does (everything in IE
// rises), which is what makes a weeping thing read at a glance.
function Drips({ color, from = 0.3, seed = 1 }) {
  const drops = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    drops.current.forEach((d, i) => {
      if (!d) return;
      const k = ((t * 0.5 + i * 0.37 + seed) % 1);
      d.position.y = from - k * from;
      d.material.opacity = 0.9 * (1 - k);
    });
  });
  return [0, 1, 2].map((i) => (
    <mesh key={i} ref={(el) => (drops.current[i] = el)} position={[0.01 * i, from, 0.02]} raycast={NO_RAY}>
      <sphereGeometry args={[0.014, 6, 5]} />
      <meshBasicMaterial color={color} transparent opacity={0.8} toneMapped={false} depthWrite={false} />
    </mesh>
  ));
}
// A necklace on a small stand: a chain of beads in a loop, a jewel hung
// at the bottom. `gem` and `drip` come off the card's own text.
function Necklace({ gem, chain = "#c9a84c", drip = null, seed = 1 }) {
  const jewel = useRef();
  usePulse(jewel, 0.9, 0.35, 2.1, seed);
  const loop = useMemo(() => {
    const pts = [];
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2;
      pts.push([Math.cos(a) * 0.17, 0.42 + Math.sin(a) * 0.14, 0]);
    }
    return pts;
  }, []);
  return (
    <group>
      {/* the stand: a slim stone post */}
      <mesh position={[0, 0.2, 0]} castShadow raycast={NO_RAY}>
        <cylinderGeometry args={[0.03, 0.05, 0.4, 8]} />
        <meshStandardMaterial map={stoneTex()} {...STONE} />
      </mesh>
      <mesh position={[0, 0.58, 0]} raycast={NO_RAY}>
        <torusGeometry args={[0.03, 0.012, 6, 12]} />
        <meshStandardMaterial map={metalTex()} {...METAL} />
      </mesh>
      <Beads pts={loop} color={chain} r0={0.016} r1={0.016} mat={METAL} />
      <mesh ref={jewel} position={[0, 0.25, 0.01]} raycast={NO_RAY}>
        <octahedronGeometry args={[0.07, 0]} />
        <meshStandardMaterial {...GLOW(gem, 0.9)} roughness={0.2} metalness={0.3} />
      </mesh>
      {drip && <Drips color={drip} from={0.24} seed={seed} />}
    </group>
  );
}
// A blade standing point-down in the card, the hilt up. `edge` is the
// emissive rim; `wisp` adds smoke (Sword of Shadows) and `bolt` a
// crackling flicker (Lightning Blade).
function Blade({ steel = "#c8ccd8", edge = null, hilt = "#5a3a1c", guard = "#c9a84c", len = 0.62, wide = 0.09, bolt = false, wisp = null, seed = 2 }) {
  const edgeRef = useRef();
  const wisps = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    const m = edgeRef.current && edgeRef.current.material;
    if (m && edge) m.emissiveIntensity = bolt ? (0.6 + (Math.sin(t * 23 + seed) > 0.6 ? 1.4 : 0) + Math.sin(t * 3) * 0.2) : 0.7 + Math.sin(t * 1.6 + seed) * 0.25;
    wisps.current.forEach((w, i) => {
      if (!w) return;
      const k = (t * 0.35 + i * 0.31 + seed) % 1;
      w.position.set(Math.sin(t * 1.3 + i) * 0.05, 0.12 + k * len, Math.cos(t * 1.1 + i) * 0.05);
      w.material.opacity = 0.35 * Math.sin(k * Math.PI);
      w.scale.setScalar(0.6 + k * 0.9);
    });
  });
  return (
    <group rotation={[0, 0, 0.12]}>
      <mesh position={[0, len / 2 + 0.02, 0]} castShadow raycast={NO_RAY}>
        <boxGeometry args={[wide, len, 0.014]} />
        <meshStandardMaterial map={metalTex()} color={steel} {...METAL} />
      </mesh>
      {edge && (
        <mesh ref={edgeRef} position={[0, len / 2 + 0.02, 0]} raycast={NO_RAY}>
          <boxGeometry args={[wide * 0.35, len * 0.96, 0.02]} />
          <meshStandardMaterial {...GLOW(edge, 0.8)} transparent opacity={0.85} />
        </mesh>
      )}
      <mesh position={[0, len + 0.05, 0]} raycast={NO_RAY}>
        <boxGeometry args={[wide * 2.6, 0.035, 0.035]} />
        <meshStandardMaterial color={guard} {...METAL} />
      </mesh>
      <mesh position={[0, len + 0.15, 0]} raycast={NO_RAY}>
        <capsuleGeometry args={[0.022, 0.16, 4, 8]} />
        <meshStandardMaterial color={hilt} roughness={0.8} />
      </mesh>
      <mesh position={[0, len + 0.26, 0]} raycast={NO_RAY}>
        <sphereGeometry args={[0.03, 8, 6]} />
        <meshStandardMaterial color={guard} {...METAL} />
      </mesh>
      {wisp && [0, 1, 2, 3].map((i) => (
        <mesh key={i} ref={(el) => (wisps.current[i] = el)} raycast={NO_RAY}>
          <sphereGeometry args={[0.05, 8, 6]} />
          <meshBasicMaterial color={wisp} transparent opacity={0.3} depthWrite={false} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}
// Two gauntlets, fists closed, knuckles to the seam.
function Gauntlets({ metal = "#8a5a2c", gem = T.gold, seed = 0 }) {
  const g = useRef();
  useFrame((s) => { if (g.current) g.current.rotation.y = Math.sin(s.clock.elapsedTime * 0.8 + seed) * 0.12; });
  const fist = (sd) => (
    <group key={sd} position={[sd * 0.16, 0.14, 0]} rotation={[0.3, 0, sd * 0.1]}>
      <mesh castShadow raycast={NO_RAY}>
        <sphereGeometry args={[0.1, 10, 8]} />
        <meshStandardMaterial map={metalTex()} color={metal} {...METAL} />
      </mesh>
      <mesh position={[0, -0.02, 0.09]} raycast={NO_RAY}>
        <boxGeometry args={[0.16, 0.06, 0.05]} />
        <meshStandardMaterial map={metalTex()} color={metal} {...METAL} />
      </mesh>
      <mesh position={[0, 0.08, -0.06]} raycast={NO_RAY}>
        <cylinderGeometry args={[0.075, 0.09, 0.14, 10]} />
        <meshStandardMaterial map={metalTex()} color={metal} {...METAL} />
      </mesh>
      <mesh position={[0, 0.04, 0.09]} raycast={NO_RAY}>
        <sphereGeometry args={[0.025, 6, 5]} />
        <meshStandardMaterial {...GLOW(gem, 0.8)} />
      </mesh>
    </group>
  );
  return <group ref={g}>{fist(-1)}{fist(1)}</group>;
}
// Winged boots — and they hover, because the wearer becomes Intangible.
function Boots({ leather = "#4a2e1c", wing = "#f2ead8", seed = 3 }) {
  const g = useRef();
  const wings = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (g.current) g.current.position.y = 0.08 + Math.sin(t * 1.4 + seed) * 0.03;
    wings.current.forEach((w, i) => { if (w) w.rotation.y = (i % 2 ? -1 : 1) * (0.5 + Math.sin(t * 7 + seed) * 0.35); });
  });
  const boot = (sd, i) => (
    <group key={sd} position={[sd * 0.13, 0, 0]}>
      <mesh position={[0, 0.06, 0.03]} castShadow raycast={NO_RAY}>
        <capsuleGeometry args={[0.055, 0.12, 4, 8]} />
        <meshStandardMaterial color={leather} roughness={0.75} />
      </mesh>
      <mesh position={[0, 0.02, 0.09]} raycast={NO_RAY}>
        <sphereGeometry args={[0.06, 8, 6]} />
        <meshStandardMaterial color={leather} roughness={0.75} />
      </mesh>
      <mesh ref={(el) => (wings.current[i] = el)} position={[sd * 0.06, 0.1, -0.02]} raycast={NO_RAY}>
        <coneGeometry args={[0.035, 0.16, 6]} />
        <meshStandardMaterial color={wing} emissive={wing} emissiveIntensity={0.25} roughness={0.6} />
      </mesh>
    </group>
  );
  return <group ref={g}>{boot(-1, 0)}{boot(1, 1)}</group>;
}
// A chest plate on an armour stand; its Lyf can be lent, so it glows
// where the heart would be.
function Cuirass({ plate = "#a8342e", trim = T.gold }) {
  const heart = useRef();
  usePulse(heart, 0.8, 0.4, 1.2, 0.4);
  return (
    <group>
      <mesh position={[0, 0.1, 0]} raycast={NO_RAY}>
        <cylinderGeometry args={[0.02, 0.035, 0.2, 8]} />
        <meshStandardMaterial map={stoneTex()} {...STONE} />
      </mesh>
      <mesh position={[0, 0.36, 0]} scale={[1, 1.25, 0.62]} castShadow raycast={NO_RAY}>
        <sphereGeometry args={[0.18, 12, 10]} />
        <meshStandardMaterial map={metalTex()} color={plate} {...METAL} />
      </mesh>
      <mesh position={[0, 0.5, 0]} rotation={[Math.PI / 2, 0, 0]} raycast={NO_RAY}>
        <torusGeometry args={[0.12, 0.018, 6, 14]} />
        <meshStandardMaterial color={trim} {...METAL} />
      </mesh>
      <mesh ref={heart} position={[0, 0.38, 0.115]} raycast={NO_RAY}>
        <octahedronGeometry args={[0.035, 0]} />
        <meshStandardMaterial {...GLOW(T.gold, 0.8)} />
      </mesh>
    </group>
  );
}
// A short blade that weeps — beads falling from the tip.
function Dagger({ seed = 4 }) {
  return (
    <group>
      <Blade steel="#d8d0e0" edge={T.shadow} hilt="#2a1a3a" guard="#8a7ca8" len={0.34} wide={0.06} seed={seed} />
      <Drips color={T.blood} from={0.34} seed={seed} />
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE CONSTRUCTIONS
// ═══════════════════════════════════════════════════════════════════
// Khao crystals in a ring; the Level adds an arch (Arcadia) and a
// fountain column (Paradise). Every crystal breathes on one hook.
function KhaoGarden({ level = 1, seed = 5 }) {
  const crystals = useRef([]);
  const col = useRef();
  const R = useMemo(() => mulberry32(seed * 131), [seed]);
  const ring = useMemo(() => {
    const n = 5 + level * 2, pts = [];
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + R() * 0.3;
      const r = 0.22 + R() * 0.1;
      pts.push({ p: [Math.cos(a) * r, 0, Math.sin(a) * r], h: 0.12 + R() * 0.18 + level * 0.04, rot: R() * 0.5 - 0.25, ph: R() * 6 });
    }
    return pts;
  }, [R, level]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    crystals.current.forEach((c, i) => { if (c) c.material.emissiveIntensity = 0.45 + Math.sin(t * 1.8 + ring[i].ph) * 0.25; });
    if (col.current) col.current.position.y = 0.42 + Math.sin(t * 1.1 + seed) * 0.03;
  });
  return (
    <group>
      <mesh position={[0, 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]} raycast={NO_RAY}>
        <circleGeometry args={[0.36, 18]} />
        <meshStandardMaterial map={leafTex()} color="#6a4a9a" roughness={0.95} />
      </mesh>
      {ring.map((c, i) => (
        <mesh key={i} ref={(el) => (crystals.current[i] = el)} position={[c.p[0], c.h / 2, c.p[2]]} rotation={[c.rot, 0, -c.rot]} castShadow raycast={NO_RAY}>
          <coneGeometry args={[0.04 + level * 0.008, c.h, 5]} />
          <meshStandardMaterial {...GLOW(T.khao, 0.5)} color={T.khaoHi} roughness={0.25} transparent opacity={0.92} />
        </mesh>
      ))}
      {level >= 2 && (
        <mesh position={[0, 0.3, -0.1]} rotation={[0, 0, 0]} raycast={NO_RAY}>
          <torusGeometry args={[0.24, 0.025, 6, 16, Math.PI]} />
          <meshStandardMaterial map={stoneTex()} {...STONE} />
        </mesh>
      )}
      {level >= 3 && (
        <group>
          <mesh position={[0, 0.2, 0]} castShadow raycast={NO_RAY}>
            <cylinderGeometry args={[0.05, 0.08, 0.4, 8]} />
            <meshStandardMaterial map={stoneTex()} {...STONE} />
          </mesh>
          <mesh ref={col} position={[0, 0.42, 0]} raycast={NO_RAY}>
            <sphereGeometry args={[0.09, 12, 10]} />
            <meshStandardMaterial {...GLOW(T.khaoHi, 0.9)} transparent opacity={0.8} />
          </mesh>
        </group>
      )}
    </group>
  );
}
// The Bhodi Tree — a trunk, three leaf masses, warm motes rising.
function BhodiTree({ seed = 6 }) {
  const crown = useRef();
  const motes = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (crown.current) crown.current.rotation.z = Math.sin(t * 0.6 + seed) * 0.03;
    motes.current.forEach((m, i) => {
      if (!m) return;
      const k = (t * 0.25 + i * 0.29 + seed) % 1;
      m.position.set(Math.sin(t + i * 2) * 0.12, 0.15 + k * 0.5, Math.cos(t * 0.8 + i) * 0.12);
      m.material.opacity = 0.7 * Math.sin(k * Math.PI);
    });
  });
  return (
    <group>
      <mesh position={[0, 0.16, 0]} castShadow raycast={NO_RAY}>
        <cylinderGeometry args={[0.035, 0.07, 0.32, 8]} />
        <meshStandardMaterial color="#3a2a1c" roughness={0.9} />
      </mesh>
      <group ref={crown}>
        {[[0, 0.42, 0, 0.19], [-0.13, 0.36, 0.05, 0.13], [0.12, 0.38, -0.06, 0.14]].map(([x, y, z, r], i) => (
          <mesh key={i} position={[x, y, z]} scale={[1, 0.8, 1]} castShadow raycast={NO_RAY}>
            <sphereGeometry args={[r, 12, 9]} />
            <meshStandardMaterial map={leafTex()} color="#7fd8a0" roughness={0.9} emissive="#2a6a3a" emissiveIntensity={0.15} />
          </mesh>
        ))}
      </group>
      {[0, 1, 2, 3].map((i) => (
        <mesh key={i} ref={(el) => (motes.current[i] = el)} raycast={NO_RAY}>
          <sphereGeometry args={[0.012, 6, 5]} />
          <meshBasicMaterial color={T.animaHi} transparent opacity={0.6} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
    </group>
  );
}
// The Anima House — a jettied little house with windows that burn warm.
function AnimaHouse({ seed = 7 }) {
  const win = useRef();
  usePulse(win, 0.9, 0.3, 1.5, seed);
  return (
    <group>
      <mesh position={[0, 0.12, 0]} castShadow raycast={NO_RAY}>
        <boxGeometry args={[0.36, 0.24, 0.3]} />
        <meshStandardMaterial map={stoneTex()} {...STONE} />
      </mesh>
      <mesh position={[0, 0.3, 0]} castShadow raycast={NO_RAY}>
        <boxGeometry args={[0.4, 0.12, 0.34]} />
        <meshStandardMaterial map={stoneTex()} color="#6a5a6a" {...STONE} />
      </mesh>
      <mesh position={[0, 0.46, 0]} rotation={[0, Math.PI / 4, 0]} castShadow raycast={NO_RAY}>
        <coneGeometry args={[0.3, 0.22, 4]} />
        <meshStandardMaterial color="#4a2634" roughness={0.85} />
      </mesh>
      <mesh ref={win} position={[0, 0.3, 0.172]} raycast={NO_RAY}>
        <boxGeometry args={[0.2, 0.06, 0.005]} />
        <meshStandardMaterial {...GLOW(T.animaHi, 0.9)} />
      </mesh>
      <mesh position={[0.08, 0.1, 0.152]} raycast={NO_RAY}>
        <boxGeometry args={[0.07, 0.12, 0.005]} />
        <meshStandardMaterial {...GLOW(T.anima, 0.6)} />
      </mesh>
    </group>
  );
}
// The Stars and Skies — an orrery: two turning rings and a sun of stars.
function StarsAndSkies({ seed = 8 }) {
  const r1 = useRef(); const r2 = useRef(); const stars = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (r1.current) r1.current.rotation.set(0.6, t * 0.4 + seed, 0);
    if (r2.current) r2.current.rotation.set(-0.5, -t * 0.28 + seed, 0.4);
    stars.current.forEach((m, i) => { if (m) m.material.emissiveIntensity = 0.7 + Math.sin(t * 2.2 + i * 1.7) * 0.5; });
  });
  return (
    <group position={[0, 0.34, 0]}>
      <mesh position={[0, -0.24, 0]} raycast={NO_RAY}>
        <cylinderGeometry args={[0.02, 0.06, 0.2, 8]} />
        <meshStandardMaterial map={metalTex()} color="#c9a84c" {...METAL} />
      </mesh>
      <mesh ref={r1} raycast={NO_RAY}>
        <torusGeometry args={[0.22, 0.012, 6, 28]} />
        <meshStandardMaterial color={T.gold} {...METAL} emissive={T.gold} emissiveIntensity={0.2} />
      </mesh>
      <mesh ref={r2} raycast={NO_RAY}>
        <torusGeometry args={[0.15, 0.01, 6, 24]} />
        <meshStandardMaterial color={T.nihil} {...METAL} emissive={T.nihil} emissiveIntensity={0.2} />
      </mesh>
      {[[0, 0, 0, 0.05], [0.22, 0.05, 0, 0.02], [-0.1, -0.12, 0.1, 0.018], [0.05, 0.16, -0.08, 0.016]].map(([x, y, z, r], i) => (
        <mesh key={i} ref={(el) => (stars.current[i] = el)} position={[x, y, z]} raycast={NO_RAY}>
          <sphereGeometry args={[r, 8, 6]} />
          <meshStandardMaterial {...GLOW(i ? "#ffffff" : T.animaHi, 0.9)} />
        </mesh>
      ))}
    </group>
  );
}
// The Khao D'uhmdumn — a stone guardian that must be broken before
// anything behind it. Beb / Champion / Lord: taller, and it earns a
// shield, then a crown of Khao.
function Duhmdumn({ tier = 1, seed = 9 }) {
  const eyes = useRef();
  usePulse(eyes, 0.9, 0.5, 0.9, seed);
  const h = 0.32 + tier * 0.12;
  return (
    <group>
      <mesh position={[0, h * 0.42, 0]} scale={[1, 1.15, 0.8]} castShadow raycast={NO_RAY}>
        <sphereGeometry args={[h * 0.42, 12, 10]} />
        <meshStandardMaterial map={stoneTex()} {...STONE} />
      </mesh>
      <mesh position={[0, h * 0.95, 0]} castShadow raycast={NO_RAY}>
        <sphereGeometry args={[h * 0.22, 12, 10]} />
        <meshStandardMaterial map={stoneTex()} {...STONE} />
      </mesh>
      <mesh ref={eyes} position={[0, h * 0.97, h * 0.2]} raycast={NO_RAY}>
        <boxGeometry args={[h * 0.22, 0.02, 0.01]} />
        <meshStandardMaterial {...GLOW(T.khaoHi, 0.9)} />
      </mesh>
      {[-1, 1].map((sd) => (
        <mesh key={sd} position={[sd * h * 0.5, h * 0.42, 0]} rotation={[0, 0, sd * 0.25]} castShadow raycast={NO_RAY}>
          <capsuleGeometry args={[h * 0.11, h * 0.35, 4, 8]} />
          <meshStandardMaterial map={stoneTex()} {...STONE} />
        </mesh>
      ))}
      {tier >= 2 && (
        <mesh position={[0, h * 0.45, h * 0.36]} rotation={[0.1, 0, 0]} castShadow raycast={NO_RAY}>
          <cylinderGeometry args={[h * 0.3, h * 0.3, 0.02, 12]} />
          <meshStandardMaterial map={metalTex()} color="#6a5aa8" {...METAL} />
        </mesh>
      )}
      {tier >= 3 && (
        <mesh position={[0, h * 1.2, 0]} rotation={[Math.PI / 2, 0, 0]} raycast={NO_RAY}>
          <torusGeometry args={[h * 0.18, 0.012, 6, 12]} />
          <meshStandardMaterial {...GLOW(T.khao, 0.8)} />
        </mesh>
      )}
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE RITES — Seal · Monoliths · Mausoleum, tinted by school
// ═══════════════════════════════════════════════════════════════════
function RiteSeal({ tint, seed = 10 }) {
  const disc = useRef(); const ring = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (disc.current) disc.current.material.emissiveIntensity = 0.5 + Math.sin(t * 1.4 + seed) * 0.3;
    if (ring.current) ring.current.rotation.z = t * 0.25;
  });
  return (
    <group>
      <mesh ref={disc} position={[0, 0.012, 0]} rotation={[-Math.PI / 2, 0, 0]} raycast={NO_RAY}>
        <circleGeometry args={[0.3, 24]} />
        <meshStandardMaterial {...GLOW(tint, 0.5)} transparent opacity={0.75} depthWrite={false} />
      </mesh>
      <mesh ref={ring} position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]} raycast={NO_RAY}>
        <ringGeometry args={[0.32, 0.36, 6]} />
        <meshBasicMaterial color={tint} transparent opacity={0.9} toneMapped={false} side={THREE.DoubleSide} depthWrite={false} />
      </mesh>
    </group>
  );
}
function RiteMonoliths({ tint, seed = 11 }) {
  const runes = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    runes.current.forEach((m, i) => { if (m) m.material.emissiveIntensity = 0.5 + Math.sin(t * 1.2 + i * 2.1 + seed) * 0.4; });
  });
  return (
    <group>
      {[[-0.16, 0.05, 0.08], [0.17, -0.06, -0.06]].map(([x, z, rot], i) => (
        <group key={i} position={[x, 0, z]} rotation={[0, rot, i ? -0.06 : 0.05]}>
          <mesh position={[0, 0.27, 0]} castShadow raycast={NO_RAY}>
            <boxGeometry args={[0.12, 0.54, 0.09]} />
            <meshStandardMaterial map={stoneTex()} {...STONE} />
          </mesh>
          <mesh ref={(el) => (runes.current[i] = el)} position={[0, 0.3, 0.047]} raycast={NO_RAY}>
            <boxGeometry args={[0.04, 0.3, 0.004]} />
            <meshStandardMaterial {...GLOW(tint, 0.6)} />
          </mesh>
        </group>
      ))}
      <RiteSeal tint={tint} seed={seed} />
    </group>
  );
}
// The tomb — massing, not curves: a body, a jettied roof, two pillars,
// an iron gate, a lantern. `scale` lets the grave-court reuse it large.
function Mausoleum({ tint, seed = 12, lantern = 0.7 }) {
  const lamp = useRef();
  usePulse(lamp, lantern, 0.25, 1.3, seed);
  return (
    <group>
      <mesh position={[0, 0.2, 0]} castShadow raycast={NO_RAY}>
        <boxGeometry args={[0.44, 0.4, 0.36]} />
        <meshStandardMaterial map={stoneTex()} {...STONE} />
      </mesh>
      <mesh position={[0, 0.43, 0]} castShadow raycast={NO_RAY}>
        <boxGeometry args={[0.5, 0.06, 0.42]} />
        <meshStandardMaterial map={stoneTex()} color="#5a5060" {...STONE} />
      </mesh>
      <mesh position={[0, 0.54, 0]} rotation={[0, 0, 0]} castShadow raycast={NO_RAY}>
        <boxGeometry args={[0.4, 0.16, 0.3]} />
        <meshStandardMaterial map={stoneTex()} color="#4a4050" {...STONE} />
      </mesh>
      {[-1, 1].map((sd) => (
        <mesh key={sd} position={[sd * 0.17, 0.2, 0.2]} castShadow raycast={NO_RAY}>
          <cylinderGeometry args={[0.03, 0.035, 0.4, 8]} />
          <meshStandardMaterial map={stoneTex()} {...STONE} />
        </mesh>
      ))}
      {/* the gate: bars */}
      {[-0.06, -0.02, 0.02, 0.06].map((x) => (
        <mesh key={x} position={[x, 0.16, 0.185]} raycast={NO_RAY}>
          <cylinderGeometry args={[0.006, 0.006, 0.3, 5]} />
          <meshStandardMaterial map={metalTex()} color="#2a2a30" {...METAL} />
        </mesh>
      ))}
      <mesh position={[0, 0.02, 0.185]} raycast={NO_RAY}>
        <boxGeometry args={[0.2, 0.32, 0.01]} />
        <meshBasicMaterial color="#05030a" />
      </mesh>
      <mesh ref={lamp} position={[0, 0.36, 0.22]} raycast={NO_RAY}>
        <sphereGeometry args={[0.03, 8, 6]} />
        <meshStandardMaterial {...GLOW(tint, lantern)} />
      </mesh>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE ESSENCES — the four Esclings (SHIP 8.6)
// ═══════════════════════════════════════════════════════════════════
// "An Escling — primordial essence waiting to be Transcended." Not a
// body yet: a school's raw stuff, standing on its card until a being
// is made of it. Space folds, Phantom drifts, Dragon burns, Time turns.
function EssenceSpace({ seed = 0 }) {
  const core = useRef(); const motes = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (core.current) { core.current.rotation.set(t * 0.7, t * 1.1, 0); core.current.material.emissiveIntensity = 0.8 + Math.sin(t * 2 + seed) * 0.3; }
    motes.current.forEach((m, i) => {
      if (!m) return;
      const a = t * (1.2 + i * 0.3) + i * 2.1 + seed, r = 0.16 + i * 0.03;
      m.position.set(Math.cos(a) * r, 0.22 + Math.sin(a * 1.7) * 0.08, Math.sin(a) * r);
    });
  });
  return (
    <group>
      <mesh ref={core} position={[0, 0.22, 0]} raycast={NO_RAY}>
        <icosahedronGeometry args={[0.1, 1]} />
        <meshStandardMaterial {...GLOW(T.khaoHi, 1.2)} color="#c9b8ff" wireframe />
      </mesh>
      <mesh position={[0, 0.22, 0]} raycast={NO_RAY}>
        <sphereGeometry args={[0.06, 10, 8]} />
        <meshBasicMaterial color="#12061f" />
      </mesh>
      {[0, 1, 2].map((i) => (
        <mesh key={i} ref={(el) => (motes.current[i] = el)} raycast={NO_RAY}>
          <sphereGeometry args={[0.014, 6, 5]} />
          <meshBasicMaterial color={T.khaoHi} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}
function EssencePhantom({ seed = 0 }) {
  const wisps = useRef([]); const eyes = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    wisps.current.forEach((w, i) => {
      if (!w) return;
      w.position.set(Math.sin(t * 0.8 + i * 2 + seed) * 0.05, 0.16 + i * 0.07 + Math.sin(t * 1.3 + i) * 0.02, Math.cos(t * 0.6 + i) * 0.04);
      w.material.opacity = 0.22 + Math.sin(t * 1.1 + i * 1.4 + seed) * 0.08;
      w.scale.setScalar(1 + Math.sin(t * 0.9 + i) * 0.1);
    });
    const blink = Math.sin(t * 0.7 + seed) > 0.96 ? 0.1 : 1;
    eyes.current.forEach((e) => { if (e) e.scale.y = blink; });
  });
  return (
    <group>
      {[0, 1, 2, 3].map((i) => (
        <mesh key={i} ref={(el) => (wisps.current[i] = el)} raycast={NO_RAY}>
          <sphereGeometry args={[0.11 - i * 0.015, 10, 8]} />
          <meshBasicMaterial color="#e8f4ff" transparent opacity={0.25} depthWrite={false} toneMapped={false} />
        </mesh>
      ))}
      {[-1, 1].map((sd, i) => (
        <mesh key={sd} ref={(el) => (eyes.current[i] = el)} position={[sd * 0.035, 0.3, 0.09]} raycast={NO_RAY}>
          <sphereGeometry args={[0.014, 6, 5]} />
          <meshBasicMaterial color={T.teal} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}
function EssenceDragon({ seed = 0 }) {
  const tongues = useRef([]); const ember = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    tongues.current.forEach((f, i) => {
      if (!f) return;
      const k = (t * (1.1 + i * 0.13) + i * 0.4 + seed) % 1;
      f.position.set(Math.sin(t * 5 + i * 2) * 0.03, 0.04 + k * 0.32, Math.cos(t * 4 + i) * 0.03);
      f.scale.set(1 - k * 0.7, 1 + k * 0.6, 1 - k * 0.7);
      f.material.opacity = 0.85 * (1 - k * k);
    });
    if (ember.current) ember.current.material.emissiveIntensity = 1 + Math.sin(t * 6 + seed) * 0.4;
  });
  return (
    <group>
      <mesh ref={ember} position={[0, 0.06, 0]} raycast={NO_RAY}>
        <sphereGeometry args={[0.07, 10, 8]} />
        <meshStandardMaterial {...GLOW("#ff9b2e", 1)} color="#4a120c" />
      </mesh>
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={i} ref={(el) => (tongues.current[i] = el)} raycast={NO_RAY}>
          <coneGeometry args={[0.05, 0.16, 6]} />
          <meshBasicMaterial color={i % 2 ? "#ffd08a" : "#e84a3a"} transparent opacity={0.8} depthWrite={false} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}
function EssenceTime({ seed = 0 }) {
  const gear = useRef(); const inner = useRef(); const pend = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (gear.current) gear.current.rotation.z = t * 0.6 + seed;
    if (inner.current) inner.current.rotation.z = -t * 1.4 + seed;
    if (pend.current) pend.current.rotation.z = Math.sin(t * 2.2 + seed) * 0.5;
  });
  return (
    <group position={[0, 0.2, 0]}>
      <mesh ref={gear} raycast={NO_RAY}>
        <torusGeometry args={[0.13, 0.025, 6, 8]} />
        <meshStandardMaterial map={metalTex()} color="#d8b24a" {...METAL} />
      </mesh>
      <mesh ref={inner} raycast={NO_RAY}>
        <torusGeometry args={[0.07, 0.018, 6, 6]} />
        <meshStandardMaterial map={metalTex()} color="#8a93a8" {...METAL} emissive="#4ae8ff" emissiveIntensity={0.3} />
      </mesh>
      <group ref={pend} position={[0, 0.13, 0]}>
        <mesh position={[0, -0.16, 0]} raycast={NO_RAY}>
          <cylinderGeometry args={[0.006, 0.006, 0.3, 5]} />
          <meshStandardMaterial map={metalTex()} color="#8a93a8" {...METAL} />
        </mesh>
        <mesh position={[0, -0.32, 0]} raycast={NO_RAY}>
          <sphereGeometry args={[0.028, 8, 6]} />
          <meshStandardMaterial {...GLOW("#4ae8ff", 0.9)} />
        </mesh>
      </group>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE TABLETS — the Prophecies (SHIP 8.6)
// ═══════════════════════════════════════════════════════════════════
// A Prophecy does not arrive; it is DECLARED (H24). So the tablet hovers
// — a rounded stone slab turning slowly above its card, a glyph burning
// on its face, immune to everything (R74) and behaving like it knows.
function glyphTex(glyph, tint) {
  return canvasTex(`sf_tablet_${glyph}_${tint}`, 128, 160, (g, w, h) => {
    g.fillStyle = "#2a2434"; g.fillRect(0, 0, w, h);
    const R = mulberry32(glyph.charCodeAt(0) * 7);
    for (let i = 0; i < 300; i++) { g.fillStyle = R() > 0.5 ? "rgba(255,240,220,0.05)" : "rgba(0,0,10,0.12)"; g.fillRect(R() * w, R() * h, 2 + R() * 5, 1 + R() * 3); }
    g.strokeStyle = tint; g.lineWidth = 2; g.globalAlpha = 0.5;
    g.strokeRect(10, 10, w - 20, h - 20);
    g.globalAlpha = 1;
    // ✦ ♆ ⧗ live in the Symbols blocks; a bare `serif` on a Windows canvas
    // may draw them as tofu, so the symbol fonts are named first.
    g.fillStyle = tint; g.font = "bold 72px 'Segoe UI Symbol', 'Noto Sans Symbols', 'Apple Symbols', serif";
    g.textAlign = "center"; g.textBaseline = "middle";
    g.shadowColor = tint; g.shadowBlur = 18;
    g.fillText(glyph, w / 2, h / 2 + 4);
  });
}
function Tablet({ glyph = "✦", tint = T.gold, seed = 0, size = 1.35 }) {
  const g = useRef(); const face = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (g.current) { g.current.position.y = 0.34 + Math.sin(t * 0.9 + seed) * 0.03; g.current.rotation.y = Math.sin(t * 0.35 + seed) * 0.5; }
    if (face.current) face.current.material.emissiveIntensity = 0.5 + Math.sin(t * 1.6 + seed) * 0.25;
  });
  return (
    <group ref={g} position={[0, 0.34, 0]} scale={size}>
      <mesh castShadow raycast={NO_RAY}>
        <boxGeometry args={[0.3, 0.4, 0.05]} />
        <meshStandardMaterial map={stoneTex()} {...STONE} />
      </mesh>
      <mesh position={[0, 0.2, 0]} rotation={[Math.PI / 2, 0, 0]} raycast={NO_RAY}>
        <cylinderGeometry args={[0.15, 0.15, 0.05, 16, 1, false, 0, Math.PI]} />
        <meshStandardMaterial map={stoneTex()} {...STONE} />
      </mesh>
      <mesh ref={face} position={[0, 0.02, 0.027]} raycast={NO_RAY}>
        <planeGeometry args={[0.26, 0.34]} />
        <meshStandardMaterial map={glyphTex(glyph, tint)} emissiveMap={glyphTex(glyph, tint)} emissive="#ffffff" emissiveIntensity={0.9} toneMapped={false} />
      </mesh>
      <mesh position={[0, -0.32, 0]} rotation={[-Math.PI / 2, 0, 0]} raycast={NO_RAY}>
        <ringGeometry args={[0.16, 0.2, 24]} />
        <meshBasicMaterial color={tint} transparent opacity={0.35} depthWrite={false} toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE CURIOS (SHIP 8.6) — what you are WEARING, set beside you
// ═══════════════════════════════════════════════════════════════════
function WizardsCap({ seed = 0 }) {
  const g = useRef(); const star = useRef();
  useFrame((s) => { const t = s.clock.elapsedTime; if (g.current) g.current.rotation.y = t * 0.3 + seed; if (star.current) star.current.material.emissiveIntensity = 0.7 + Math.sin(t * 3 + seed) * 0.4; });
  return (
    <group ref={g}>
      <mesh position={[0, 0.03, 0]} raycast={NO_RAY}>
        <cylinderGeometry args={[0.22, 0.24, 0.03, 18]} />
        <meshStandardMaterial color="#2a1a4a" roughness={0.85} />
      </mesh>
      <mesh position={[0, 0.22, 0]} rotation={[0, 0, 0.12]} castShadow raycast={NO_RAY}>
        <coneGeometry args={[0.13, 0.4, 14]} />
        <meshStandardMaterial color="#3a2468" roughness={0.85} />
      </mesh>
      <mesh ref={star} position={[0.06, 0.2, 0.1]} raycast={NO_RAY}>
        <octahedronGeometry args={[0.03, 0]} />
        <meshStandardMaterial {...GLOW(T.gold, 0.8)} />
      </mesh>
    </group>
  );
}
function CircleOfLyf({ seed = 0 }) {
  const r = useRef();
  useFrame((s) => { const t = s.clock.elapsedTime; if (r.current) { r.current.rotation.y = t * 0.5 + seed; r.current.material.emissiveIntensity = 0.7 + Math.sin(t * 1.5 + seed) * 0.3; } });
  return (
    <group position={[0, 0.22, 0]}>
      <mesh ref={r} raycast={NO_RAY}>
        <torusGeometry args={[0.16, 0.022, 8, 32]} />
        <meshStandardMaterial {...GLOW("#4a9fd4", 0.7)} color="#a8d8ff" metalness={0.4} roughness={0.3} />
      </mesh>
      <mesh position={[0, -0.22, 0]} raycast={NO_RAY}>
        <cylinderGeometry args={[0.02, 0.04, 0.06, 8]} />
        <meshStandardMaterial map={stoneTex()} {...STONE} />
      </mesh>
    </group>
  );
}
function JynxisWares({ seed = 0 }) {
  const coins = useRef([]);
  useFrame((s) => { const t = s.clock.elapsedTime; coins.current.forEach((c, i) => { if (c) c.rotation.y = t * (1 + i * 0.3) + seed; }); });
  return (
    <group>
      <mesh position={[0, 0.13, 0]} scale={[1, 1.1, 0.9]} castShadow raycast={NO_RAY}>
        <sphereGeometry args={[0.15, 12, 10]} />
        <meshStandardMaterial color="#6a4a2a" roughness={0.95} />
      </mesh>
      <mesh position={[0, 0.3, 0]} raycast={NO_RAY}>
        <cylinderGeometry args={[0.05, 0.07, 0.06, 10]} />
        <meshStandardMaterial color="#4a3018" roughness={0.95} />
      </mesh>
      {[[0.16, 0.02, 0.08], [-0.14, 0.02, 0.1], [0.05, 0.02, 0.19]].map((p, i) => (
        <mesh key={i} ref={(el) => (coins.current[i] = el)} position={p} rotation={[0.3, 0, 0.2]} raycast={NO_RAY}>
          <cylinderGeometry args={[0.035, 0.035, 0.008, 12]} />
          <meshStandardMaterial map={metalTex()} color="#e8c04c" {...METAL} emissive="#a06a10" emissiveIntensity={0.2} />
        </mesh>
      ))}
    </group>
  );
}
function Spectacles({ seed = 0 }) {
  const g = useRef();
  useFrame((s) => { if (g.current) g.current.rotation.y = Math.sin(s.clock.elapsedTime * 0.6 + seed) * 0.35; });
  return (
    <group ref={g} position={[0, 0.14, 0]} rotation={[0.3, 0, 0]}>
      {[-1, 1].map((sd) => (
        <mesh key={sd} position={[sd * 0.09, 0, 0]} raycast={NO_RAY}>
          <torusGeometry args={[0.07, 0.008, 6, 20]} />
          <meshStandardMaterial map={metalTex()} color="#c9a84c" {...METAL} />
        </mesh>
      ))}
      {[-1, 1].map((sd) => (
        <mesh key={sd} position={[sd * 0.09, 0, 0]} raycast={NO_RAY}>
          <circleGeometry args={[0.065, 16]} />
          <meshStandardMaterial color="#8ad8ff" transparent opacity={0.35} metalness={0.2} roughness={0.1} side={THREE.DoubleSide} />
        </mesh>
      ))}
      <mesh rotation={[0, 0, Math.PI / 2]} raycast={NO_RAY}>
        <cylinderGeometry args={[0.005, 0.005, 0.05, 5]} />
        <meshStandardMaterial map={metalTex()} color="#c9a84c" {...METAL} />
      </mesh>
    </group>
  );
}
function Elixir({ seed = 0 }) {
  const liquid = useRef(); const bubbles = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (liquid.current) liquid.current.material.emissiveIntensity = 0.6 + Math.sin(t * 2 + seed) * 0.25;
    bubbles.current.forEach((b, i) => { if (!b) return; const k = (t * 0.4 + i * 0.33 + seed) % 1; b.position.set(Math.sin(i * 2) * 0.03, 0.06 + k * 0.16, Math.cos(i * 3) * 0.03); b.material.opacity = 0.6 * Math.sin(k * Math.PI); });
  });
  return (
    <group>
      <mesh position={[0, 0.12, 0]} raycast={NO_RAY}>
        <sphereGeometry args={[0.1, 12, 10]} />
        <meshStandardMaterial color="#c8e8ff" transparent opacity={0.3} roughness={0.05} metalness={0.1} />
      </mesh>
      <mesh ref={liquid} position={[0, 0.1, 0]} scale={[0.85, 0.7, 0.85]} raycast={NO_RAY}>
        <sphereGeometry args={[0.1, 12, 10]} />
        <meshStandardMaterial {...GLOW("#ff5a7a", 0.6)} color="#b0103a" transparent opacity={0.85} />
      </mesh>
      <mesh position={[0, 0.26, 0]} raycast={NO_RAY}>
        <cylinderGeometry args={[0.03, 0.035, 0.1, 10]} />
        <meshStandardMaterial color="#c8e8ff" transparent opacity={0.35} roughness={0.05} />
      </mesh>
      <mesh position={[0, 0.33, 0]} raycast={NO_RAY}>
        <cylinderGeometry args={[0.035, 0.03, 0.04, 10]} />
        <meshStandardMaterial color="#5a3a1c" roughness={0.9} />
      </mesh>
      {[0, 1, 2].map((i) => (
        <mesh key={i} ref={(el) => (bubbles.current[i] = el)} raycast={NO_RAY}>
          <sphereGeometry args={[0.01, 6, 5]} />
          <meshBasicMaterial color="#ffd0e0" transparent opacity={0.5} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
    </group>
  );
}
function WarlocksRobe({ seed = 0 }) {
  const hem = useRef();
  useFrame((s) => { if (hem.current) hem.current.material.emissiveIntensity = 0.3 + Math.sin(s.clock.elapsedTime * 1.2 + seed) * 0.15; });
  return (
    <group>
      <mesh position={[0, 0.24, 0]} castShadow raycast={NO_RAY}>
        <coneGeometry args={[0.2, 0.48, 14]} />
        <meshStandardMaterial map={stoneTex()} color="#2a1030" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.5, 0]} raycast={NO_RAY}>
        <coneGeometry args={[0.09, 0.14, 12]} />
        <meshStandardMaterial color="#2a1030" roughness={0.9} />
      </mesh>
      <mesh ref={hem} position={[0, 0.03, 0]} rotation={[Math.PI / 2, 0, 0]} raycast={NO_RAY}>
        <torusGeometry args={[0.19, 0.012, 6, 24]} />
        <meshStandardMaterial {...GLOW("#7c3aed", 0.3)} />
      </mesh>
    </group>
  );
}
function BebBuddy({ seed = 0 }) {
  const g = useRef(); const eyes = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (g.current) { g.current.position.y = Math.abs(Math.sin(t * 3 + seed)) * 0.05; g.current.rotation.z = Math.sin(t * 1.5 + seed) * 0.12; }
    const blink = Math.sin(t * 0.9 + seed) > 0.95 ? 0.1 : 1;
    eyes.current.forEach((e) => { if (e) e.scale.y = blink; });
  });
  return (
    <group ref={g}>
      <mesh position={[0, 0.13, 0]} scale={[1, 1.15, 0.95]} castShadow raycast={NO_RAY}>
        <sphereGeometry args={[0.12, 12, 10]} />
        <meshStandardMaterial color="#ffd6e0" roughness={0.8} />
      </mesh>
      {[-1, 1].map((sd, i) => (
        <mesh key={sd} ref={(el) => (eyes.current[i] = el)} position={[sd * 0.045, 0.16, 0.105]} raycast={NO_RAY}>
          <sphereGeometry args={[0.018, 8, 6]} />
          <meshBasicMaterial color="#1a0a20" />
        </mesh>
      ))}
      {[-1, 1].map((sd) => (
        <mesh key={sd} position={[sd * 0.07, 0.26, 0]} rotation={[0, 0, sd * -0.4]} raycast={NO_RAY}>
          <coneGeometry args={[0.03, 0.08, 6]} />
          <meshStandardMaterial color="#ffd6e0" roughness={0.8} />
        </mesh>
      ))}
    </group>
  );
}
function BadassBeb({ seed = 0 }) {
  const g = useRef();
  useFrame((s) => { if (g.current) g.current.rotation.y = s.clock.elapsedTime * 0.4 + seed; });
  return (
    <group ref={g} position={[0, 0.1, 0]}>
      <mesh rotation={[Math.PI / 2, 0, 0]} raycast={NO_RAY}>
        <torusGeometry args={[0.15, 0.03, 8, 24]} />
        <meshStandardMaterial color="#2a1a14" roughness={0.8} />
      </mesh>
      {Array.from({ length: 8 }, (_, i) => {
        const a = (i / 8) * Math.PI * 2;
        return (
          <mesh key={i} position={[Math.cos(a) * 0.17, 0, Math.sin(a) * 0.17]} rotation={[0, -a, -Math.PI / 2]} raycast={NO_RAY}>
            <coneGeometry args={[0.018, 0.07, 6]} />
            <meshStandardMaterial map={metalTex()} color="#c8ccd8" {...METAL} />
          </mesh>
        );
      })}
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE SPELLS — Nihil · Omnia · Ancient Sorcery · Shadow Gambling ·
// Contract (SHIP 8.7). Five archetypes, a `form` per card.
// ═══════════════════════════════════════════════════════════════════
// Nihil is the school of light that NULLIFIES: every Nihil Tome is a
// ward. A disc of white-blue light hanging in the air, a rune ring
// turning behind it, and one form-detail off the card: rays, a wall, a
// rain of motes, wings, a blade beside the shield.
function Ward({ form = "disc", tint = T.nihil, seed = 0 }) {
  const disc = useRef(); const ring = useRef(); const rays = useRef([]); const drops = useRef([]); const wings = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (disc.current) { disc.current.position.y = 0.34 + Math.sin(t * 1.1 + seed) * 0.02; disc.current.material.emissiveIntensity = 0.8 + Math.sin(t * 1.7 + seed) * 0.3; }
    if (ring.current) ring.current.rotation.z = t * 0.4 + seed;
    rays.current.forEach((r, i) => { if (r) { r.rotation.z = t * 0.3 + i * (Math.PI / 3); r.material.opacity = 0.25 + Math.sin(t * 2 + i) * 0.12; } });
    drops.current.forEach((d, i) => { if (!d) return; const k = (t * 0.5 + i * 0.2 + seed) % 1; d.position.set(Math.sin(i * 2.3) * 0.16, 0.62 - k * 0.6, Math.cos(i * 1.7) * 0.12); d.material.opacity = 0.7 * (1 - k); });
    wings.current.forEach((w, i) => { if (w) w.rotation.y = (i ? -1 : 1) * (0.35 + Math.sin(t * 3 + seed) * 0.3); });
  });
  return (
    <group>
      <mesh ref={disc} position={[0, 0.34, 0]} raycast={NO_RAY}>
        <cylinderGeometry args={[0.17, 0.17, 0.02, 24]} />
        <meshStandardMaterial {...GLOW(tint, 0.8)} color="#e8f6ff" transparent opacity={0.85} metalness={0.3} roughness={0.2} />
      </mesh>
      <mesh ref={ring} position={[0, 0.34, -0.03]} raycast={NO_RAY}>
        <ringGeometry args={[0.2, 0.24, 6]} />
        <meshBasicMaterial color={tint} transparent opacity={0.7} toneMapped={false} side={THREE.DoubleSide} depthWrite={false} />
      </mesh>
      {form === "rays" && [0, 1, 2].map((i) => (
        <mesh key={i} ref={(el) => (rays.current[i] = el)} position={[0, 0.34, -0.02]} raycast={NO_RAY}>
          <planeGeometry args={[0.06, 0.7]} />
          <meshBasicMaterial color={tint} transparent opacity={0.3} toneMapped={false} depthWrite={false} side={THREE.DoubleSide} />
        </mesh>
      ))}
      {form === "wall" && (
        <mesh position={[0, 0.28, 0]} raycast={NO_RAY}>
          <boxGeometry args={[0.6, 0.5, 0.03]} />
          <meshStandardMaterial {...GLOW(tint, 0.5)} color="#dff2ff" transparent opacity={0.45} />
        </mesh>
      )}
      {form === "rain" && [0, 1, 2, 3, 4].map((i) => (
        <mesh key={i} ref={(el) => (drops.current[i] = el)} raycast={NO_RAY}>
          <sphereGeometry args={[0.014, 6, 5]} />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.6} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {form === "wings" && [-1, 1].map((sd, i) => (
        <mesh key={sd} ref={(el) => (wings.current[i] = el)} position={[sd * 0.2, 0.36, 0]} rotation={[0, 0, sd * 0.3]} raycast={NO_RAY}>
          <coneGeometry args={[0.06, 0.34, 6]} />
          <meshStandardMaterial {...GLOW("#ffd0a0", 0.6)} color="#fff2e0" transparent opacity={0.8} />
        </mesh>
      ))}
      {form === "blade" && <group position={[0.22, 0, 0]} scale={0.7}><Blade steel="#f2f8ff" edge={tint} guard="#c9d8ff" len={0.5} wide={0.07} seed={seed} /></group>}
      {form === "boots" && <group scale={0.9}><Boots leather="#e8f0ff" wing={tint} seed={seed} /></group>}
      {form === "scales" && (
        <group position={[0, 0.34, 0.06]}>
          <mesh rotation={[0, 0, Math.PI / 2]} raycast={NO_RAY}><cylinderGeometry args={[0.008, 0.008, 0.4, 5]} /><meshStandardMaterial map={metalTex()} color="#c9d8ff" {...METAL} /></mesh>
          {[-1, 1].map((sd) => (
            <mesh key={sd} position={[sd * 0.2, -0.1, 0]} rotation={[Math.PI / 2, 0, 0]} raycast={NO_RAY}><cylinderGeometry args={[0.06, 0.06, 0.01, 12]} /><meshStandardMaterial map={metalTex()} color="#c9d8ff" {...METAL} /></mesh>
          ))}
        </group>
      )}
    </group>
  );
}
// Omnia is the school that RUINS: every Omnia Tome is an omen — a black
// obsidian shape turning slowly above the card with a red seam of light
// through it. The form is the card's: a blade, a crown, a jet, a star.
function Omen({ form = "blade", seed = 0 }) {
  const g = useRef(); const seam = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (g.current) { g.current.rotation.y = t * 0.5 + seed; g.current.position.y = 0.32 + Math.sin(t * 0.8 + seed) * 0.02; }
    if (seam.current) seam.current.material.emissiveIntensity = 0.9 + Math.sin(t * 2.3 + seed) * 0.5;
  });
  const OB = { color: "#0a0610", roughness: 0.25, metalness: 0.6 };
  return (
    <group ref={g} position={[0, 0.32, 0]}>
      {form === "blade" && (<>
        <mesh raycast={NO_RAY}><boxGeometry args={[0.07, 0.5, 0.03]} /><meshStandardMaterial {...OB} /></mesh>
        <mesh ref={seam} raycast={NO_RAY}><boxGeometry args={[0.015, 0.48, 0.035]} /><meshStandardMaterial {...GLOW(T.blood, 0.9)} /></mesh>
        <mesh position={[0, -0.27, 0]} raycast={NO_RAY}><boxGeometry args={[0.2, 0.03, 0.03]} /><meshStandardMaterial {...OB} /></mesh>
      </>)}
      {form === "crown" && (<>
        <mesh rotation={[Math.PI / 2, 0, 0]} raycast={NO_RAY}><torusGeometry args={[0.14, 0.03, 8, 8]} /><meshStandardMaterial {...OB} /></mesh>
        {Array.from({ length: 6 }, (_, i) => { const a = (i / 6) * Math.PI * 2; return (
          <mesh key={i} position={[Math.cos(a) * 0.14, 0.08, Math.sin(a) * 0.14]} raycast={NO_RAY}><coneGeometry args={[0.025, 0.14, 4]} /><meshStandardMaterial {...OB} /></mesh>); })}
        <mesh ref={seam} rotation={[Math.PI / 2, 0, 0]} raycast={NO_RAY}><torusGeometry args={[0.14, 0.008, 6, 24]} /><meshStandardMaterial {...GLOW(T.blood, 0.9)} /></mesh>
      </>)}
      {form === "jet" && (<>
        <mesh rotation={[Math.PI / 2, 0, 0]} raycast={NO_RAY}><coneGeometry args={[0.06, 0.5, 8]} /><meshStandardMaterial {...OB} /></mesh>
        <mesh ref={seam} position={[0, 0, 0.3]} raycast={NO_RAY}><sphereGeometry args={[0.04, 8, 6]} /><meshStandardMaterial {...GLOW("#7c3aed", 0.9)} /></mesh>
      </>)}
      {form === "star" && (<>
        <mesh raycast={NO_RAY}><octahedronGeometry args={[0.16, 0]} /><meshStandardMaterial {...OB} /></mesh>
        <mesh ref={seam} raycast={NO_RAY}><octahedronGeometry args={[0.08, 0]} /><meshStandardMaterial {...GLOW(T.blood, 0.9)} /></mesh>
      </>)}
      {form === "armor" && (<>
        <mesh scale={[1, 1.2, 0.6]} raycast={NO_RAY}><sphereGeometry args={[0.16, 12, 10]} /><meshStandardMaterial {...OB} /></mesh>
        <mesh ref={seam} position={[0, 0, 0.1]} raycast={NO_RAY}><boxGeometry args={[0.015, 0.25, 0.01]} /><meshStandardMaterial {...GLOW(T.blood, 0.9)} /></mesh>
      </>)}
      {form === "wall" && (<>
        <mesh raycast={NO_RAY}><boxGeometry args={[0.55, 0.45, 0.04]} /><meshStandardMaterial {...OB} /></mesh>
        <mesh ref={seam} raycast={NO_RAY}><boxGeometry args={[0.5, 0.01, 0.045]} /><meshStandardMaterial {...GLOW(T.blood, 0.9)} /></mesh>
      </>)}
      {form === "scales" && (<>
        <mesh rotation={[0, 0, Math.PI / 2]} raycast={NO_RAY}><cylinderGeometry args={[0.01, 0.01, 0.42, 5]} /><meshStandardMaterial {...OB} /></mesh>
        {[-1, 1].map((sd) => (<mesh key={sd} position={[sd * 0.2, -0.12, 0]} rotation={[Math.PI / 2, 0, 0]} raycast={NO_RAY}><cylinderGeometry args={[0.06, 0.06, 0.012, 12]} /><meshStandardMaterial {...OB} /></mesh>))}
        <mesh ref={seam} position={[0, 0.06, 0]} raycast={NO_RAY}><sphereGeometry args={[0.03, 8, 6]} /><meshStandardMaterial {...GLOW(T.blood, 0.9)} /></mesh>
      </>)}
      {form === "sword_shield" && (<>
        <mesh position={[-0.1, 0, 0]} scale={[1, 1.2, 0.5]} raycast={NO_RAY}><sphereGeometry args={[0.13, 12, 10]} /><meshStandardMaterial {...OB} /></mesh>
        <mesh position={[0.12, 0, 0]} rotation={[0, 0, 0.2]} raycast={NO_RAY}><boxGeometry args={[0.05, 0.46, 0.025]} /><meshStandardMaterial {...OB} /></mesh>
        <mesh ref={seam} position={[0.12, 0, 0]} rotation={[0, 0, 0.2]} raycast={NO_RAY}><boxGeometry args={[0.012, 0.44, 0.03]} /><meshStandardMaterial {...GLOW(T.blood, 0.9)} /></mesh>
      </>)}
    </group>
  );
}
// Ancient Sorcery SEARCHES and MOVES cards: a grimoire, open, with pages
// of light lifting off it. `tint` is the card's — the blood Tomes bleed.
function Grimoire({ tint = "#c9b8ff", blood = false, seed = 0 }) {
  const pages = useRef([]); const glow = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    pages.current.forEach((pg, i) => { if (!pg) return; const k = (t * 0.3 + i * 0.33 + seed) % 1; pg.position.set(Math.sin(t + i * 2) * 0.06, 0.16 + k * 0.4, Math.cos(t * 0.7 + i) * 0.06); pg.rotation.set(0.3, t * 0.8 + i, 0.2); pg.material.opacity = 0.7 * Math.sin(k * Math.PI); });
    if (glow.current) glow.current.material.emissiveIntensity = 0.5 + Math.sin(t * 1.4 + seed) * 0.25;
  });
  return (
    <group>
      {[-1, 1].map((sd) => (
        <mesh key={sd} position={[sd * 0.13, 0.06, 0]} rotation={[0, 0, sd * -0.35]} castShadow raycast={NO_RAY}>
          <boxGeometry args={[0.26, 0.03, 0.34]} />
          <meshStandardMaterial color={blood ? "#4a1018" : "#2a1a3a"} roughness={0.85} />
        </mesh>
      ))}
      <mesh ref={glow} position={[0, 0.085, 0]} rotation={[-Math.PI / 2, 0, 0]} raycast={NO_RAY}>
        <planeGeometry args={[0.22, 0.3]} />
        <meshStandardMaterial {...GLOW(tint, 0.5)} color="#f8f0ff" transparent opacity={0.8} />
      </mesh>
      {[0, 1, 2].map((i) => (
        <mesh key={i} ref={(el) => (pages.current[i] = el)} raycast={NO_RAY}>
          <planeGeometry args={[0.1, 0.13]} />
          <meshBasicMaterial color={tint} transparent opacity={0.5} toneMapped={false} depthWrite={false} side={THREE.DoubleSide} />
        </mesh>
      ))}
      {blood && <Drips color={T.blood} from={0.16} seed={seed} />}
    </group>
  );
}
// Shadow Gambling ROLLS, FLIPS and DEALS: dice tumbling, a coin turning
// over, a fan of cards, and — for Gambit — a beckoning cat.
function Gamble({ form = "dice", seed = 0 }) {
  const dice = useRef([]); const coin = useRef(); const paw = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    dice.current.forEach((d, i) => { if (!d) return; d.rotation.set(t * (0.9 + i * 0.4) + seed, t * 0.7 + i, 0); d.position.y = 0.18 + Math.abs(Math.sin(t * 2.2 + i * 1.5 + seed)) * 0.08; });
    if (coin.current) { coin.current.rotation.x = t * 3 + seed; coin.current.position.y = 0.2 + Math.abs(Math.sin(t * 1.5 + seed)) * 0.16; }
    if (paw.current) paw.current.rotation.x = -0.6 + Math.sin(t * 2.4 + seed) * 0.5;
  });
  const pip = (x, y, z) => (<mesh key={`${x}${y}${z}`} position={[x, y, z]} raycast={NO_RAY}><sphereGeometry args={[0.012, 6, 5]} /><meshBasicMaterial color="#1a0a20" /></mesh>);
  const die = (i, x) => (
    <group key={i} ref={(el) => (dice.current[i] = el)} position={[x, 0.18, 0]}>
      <mesh castShadow raycast={NO_RAY}><boxGeometry args={[0.1, 0.1, 0.1]} /><meshStandardMaterial color="#f4ecdc" roughness={0.5} /></mesh>
      {pip(0, 0.051, 0)}{pip(0.03, 0, 0.051)}{pip(-0.03, 0, 0.051)}{pip(0.051, 0.03, 0.03)}{pip(0.051, -0.03, -0.03)}
    </group>
  );
  return (
    <group>
      {form === "dice" && (<>{die(0, -0.09)}{die(1, 0.09)}</>)}
      {form === "die" && die(0, 0)}
      {form === "coin" && (
        <mesh ref={coin} position={[0, 0.2, 0]} raycast={NO_RAY}>
          <cylinderGeometry args={[0.09, 0.09, 0.012, 20]} />
          <meshStandardMaterial map={metalTex()} color="#e8c04c" {...METAL} emissive="#a06a10" emissiveIntensity={0.25} />
        </mesh>
      )}
      {form === "cards" && [-2, -1, 0, 1, 2].map((i) => (
        <mesh key={i} position={[i * 0.05, 0.2 + Math.abs(i) * -0.01, i * 0.01]} rotation={[0.2, 0, i * 0.18]} raycast={NO_RAY}>
          <boxGeometry args={[0.12, 0.17, 0.005]} />
          <meshStandardMaterial color={i % 2 ? "#2a1a3a" : "#e8dcc8"} roughness={0.6} />
        </mesh>
      ))}
      {form === "cat" && (
        <group>
          <mesh position={[0, 0.14, 0]} scale={[1, 1.1, 0.9]} castShadow raycast={NO_RAY}><sphereGeometry args={[0.13, 12, 10]} /><meshStandardMaterial color="#f8f0e0" roughness={0.7} /></mesh>
          <mesh position={[0, 0.31, 0.02]} raycast={NO_RAY}><sphereGeometry args={[0.09, 12, 10]} /><meshStandardMaterial color="#f8f0e0" roughness={0.7} /></mesh>
          {[-1, 1].map((sd) => (<mesh key={sd} position={[sd * 0.06, 0.4, 0]} rotation={[0, 0, sd * -0.3]} raycast={NO_RAY}><coneGeometry args={[0.025, 0.06, 5]} /><meshStandardMaterial color="#f8f0e0" roughness={0.7} /></mesh>))}
          <mesh position={[0, 0.16, 0.12]} raycast={NO_RAY}><cylinderGeometry args={[0.035, 0.035, 0.01, 12]} /><meshStandardMaterial map={metalTex()} color="#e8c04c" {...METAL} /></mesh>
          <group ref={paw} position={[0.11, 0.24, 0.04]}><mesh position={[0, 0.06, 0]} raycast={NO_RAY}><capsuleGeometry args={[0.03, 0.08, 4, 8]} /><meshStandardMaterial color="#f8f0e0" roughness={0.7} /></mesh></group>
        </group>
      )}
    </group>
  );
}
// A Contract is SIGNED: a scroll with a wax seal and a quill, and the
// card's own term standing beside it — a gavel, a crown, chains, fire.
function Contract({ form = "scroll", seed = 0 }) {
  const quill = useRef(); const fire = useRef([]); const seal = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (quill.current) quill.current.rotation.z = -0.5 + Math.sin(t * 1.8 + seed) * 0.12;
    if (seal.current) seal.current.material.emissiveIntensity = 0.4 + Math.sin(t * 1.3 + seed) * 0.2;
    fire.current.forEach((f, i) => { if (!f) return; const k = (t * 0.9 + i * 0.3 + seed) % 1; f.position.set(-0.1 + i * 0.07, 0.06 + k * 0.2, 0.05); f.scale.setScalar(1 - k * 0.6); f.material.opacity = 0.8 * (1 - k); });
  });
  return (
    <group>
      <mesh position={[0, 0.04, 0]} rotation={[0, 0, Math.PI / 2]} castShadow raycast={NO_RAY}>
        <cylinderGeometry args={[0.04, 0.04, 0.34, 12]} />
        <meshStandardMaterial color="#e8d8b8" roughness={0.8} />
      </mesh>
      <mesh position={[0, 0.02, 0.1]} rotation={[-Math.PI / 2, 0, 0]} raycast={NO_RAY}>
        <planeGeometry args={[0.32, 0.2]} />
        <meshStandardMaterial color="#efe3c8" roughness={0.85} side={THREE.DoubleSide} />
      </mesh>
      <mesh ref={seal} position={[0.1, 0.03, 0.14]} rotation={[-Math.PI / 2, 0, 0]} raycast={NO_RAY}>
        <cylinderGeometry args={[0.03, 0.03, 0.01, 12]} />
        <meshStandardMaterial {...GLOW(T.blood, 0.4)} color="#8a1020" />
      </mesh>
      <group ref={quill} position={[-0.12, 0.06, 0.12]} rotation={[0, 0, -0.5]}>
        <mesh position={[0, 0.12, 0]} raycast={NO_RAY}><coneGeometry args={[0.025, 0.26, 5]} /><meshStandardMaterial color="#f4f0ff" roughness={0.6} /></mesh>
      </group>
      {form === "gavel" && (
        <group position={[0.16, 0.14, -0.06]} rotation={[0, 0, 0.4]}>
          <mesh rotation={[0, 0, Math.PI / 2]} raycast={NO_RAY}><cylinderGeometry args={[0.012, 0.012, 0.22, 6]} /><meshStandardMaterial color="#5a3a1c" roughness={0.8} /></mesh>
          <mesh position={[0.11, 0, 0]} rotation={[Math.PI / 2, 0, 0]} raycast={NO_RAY}><cylinderGeometry args={[0.04, 0.04, 0.09, 10]} /><meshStandardMaterial color="#4a2a14" roughness={0.8} /></mesh>
        </group>
      )}
      {form === "crown" && (
        <group position={[0.18, 0.1, -0.06]}>
          <mesh rotation={[Math.PI / 2, 0, 0]} raycast={NO_RAY}><torusGeometry args={[0.07, 0.02, 8, 12]} /><meshStandardMaterial map={metalTex()} color="#e8c04c" {...METAL} /></mesh>
          {[0, 1, 2, 3, 4].map((i) => { const a = (i / 5) * Math.PI * 2; return (<mesh key={i} position={[Math.cos(a) * 0.07, 0.05, Math.sin(a) * 0.07]} raycast={NO_RAY}><coneGeometry args={[0.015, 0.08, 4]} /><meshStandardMaterial map={metalTex()} color="#e8c04c" {...METAL} /></mesh>); })}
        </group>
      )}
      {form === "chains" && [0, 1, 2, 3].map((i) => (
        <mesh key={i} position={[-0.2 + i * 0.13, 0.05, -0.12]} rotation={[Math.PI / 2, i % 2 ? Math.PI / 2 : 0, 0]} raycast={NO_RAY}>
          <torusGeometry args={[0.035, 0.008, 6, 12]} />
          <meshStandardMaterial map={metalTex()} color="#6a6a74" {...METAL} />
        </mesh>
      ))}
      {form === "fire" && [0, 1, 2, 3].map((i) => (
        <mesh key={i} ref={(el) => (fire.current[i] = el)} raycast={NO_RAY}>
          <coneGeometry args={[0.035, 0.1, 6]} />
          <meshBasicMaterial color={i % 2 ? "#ffd08a" : "#ff6a2e"} transparent opacity={0.8} depthWrite={false} toneMapped={false} />
        </mesh>
      ))}
      {form === "blade" && <group position={[0.2, 0, -0.08]} scale={0.6}><Blade steel="#d8d0e0" edge={T.blood} len={0.4} wide={0.06} seed={seed} /></group>}
      {form === "mask" && (
        <mesh position={[0.18, 0.12, -0.06]} rotation={[0.3, 0, 0]} raycast={NO_RAY}>
          <sphereGeometry args={[0.07, 12, 10, 0, Math.PI]} />
          <meshStandardMaterial color="#e8dcc8" roughness={0.6} side={THREE.DoubleSide} />
        </mesh>
      )}
      {form === "two" && (
        <mesh position={[0, 0.12, -0.12]} rotation={[0, 0, Math.PI / 2]} raycast={NO_RAY}>
          <cylinderGeometry args={[0.03, 0.03, 0.3, 12]} />
          <meshStandardMaterial color="#c8b8a0" roughness={0.8} />
        </mesh>
      )}
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE REGISTRY — card id → prop. A row, never a switch.
// ═══════════════════════════════════════════════════════════════════
const PROPS = {
  // treasures — jewel / drip / edge off each card's text
  tome_necklace_death:   (s) => <Necklace gem={T.blood} chain="#3a2a3a" drip={T.blood} seed={s} />,
  tome_necklace_courage: (s) => <Necklace gem={T.gold} chain="#c9a84c" seed={s} />,
  tome_necklace_akula:   (s) => <Necklace gem={T.teal} chain="#8aa8b0" seed={s} />,
  tome_lightning_blade:  (s) => <Blade edge={T.bolt} bolt seed={s} />,
  tome_sword_of_shadows: (s) => <Blade steel="#2a2436" edge={T.shadow} guard="#5a4a7a" wisp="#7c3aed" seed={s} />,
  tome_weeping_dagger:   (s) => <Dagger seed={s} />,
  tome_gauntlets_azka:   (s) => <Gauntlets seed={s} />,
  tome_boots_wing_walking: (s) => <Boots seed={s} />,
  tome_chief_calidum:    (s) => <Cuirass />,
  // constructions
  tome_khao_garden:   (s) => <KhaoGarden level={1} seed={s} />,
  tome_khao_arcadia:  (s) => <KhaoGarden level={2} seed={s} />,
  tome_khao_paradise: (s) => <KhaoGarden level={3} seed={s} />,
  tome_bhodi_tree:    (s) => <BhodiTree seed={s} />,
  tome_anima_house:   (s) => <AnimaHouse seed={s} />,
  tome_stars_and_skies: (s) => <StarsAndSkies seed={s} />,
  tome_khao_duhmdumn_beb:      (s) => <Duhmdumn tier={1} seed={s} />,
  tome_khao_duhmdumn_champion: (s) => <Duhmdumn tier={2} seed={s} />,
  tome_khao_duhmdumn_lord:     (s) => <Duhmdumn tier={3} seed={s} />,
  // rites
  tome_seal_of_omnis:        (s) => <RiteSeal tint={T.omnis} seed={s} />,
  tome_monoliths_of_omnis:   (s) => <RiteMonoliths tint={T.omnis} seed={s} />,
  tome_mausoleum_of_omnis:   (s) => <Mausoleum tint={T.omnis} seed={s} />,
  tome_seal_of_nihil:        (s) => <RiteSeal tint={T.nihil} seed={s} />,
  tome_monoliths_of_nihil:   (s) => <RiteMonoliths tint={T.nihil} seed={s} />,
  tome_mausoleum_of_nihil:   (s) => <Mausoleum tint={T.nihil} seed={s} />,
  tome_soulcerers_seal:      (s) => <RiteSeal tint={T.soul} seed={s} />,
  tome_soulcerers_monoliths: (s) => <RiteMonoliths tint={T.soul} seed={s} />,
  tome_soulcerers_mausoleum: (s) => <Mausoleum tint={T.soul} seed={s} />,
  // essences (8.6)
  // (scaled up as a group — an essence is a small thing but it must read
  //  from the table camera; the dial is one number, here)
  escling_space:   (s) => <group scale={1.6}><EssenceSpace seed={s} /></group>,
  escling_phantom: (s) => <group scale={1.6}><EssencePhantom seed={s} /></group>,
  escling_dragon:  (s) => <group scale={1.6}><EssenceDragon seed={s} /></group>,
  escling_time:    (s) => <group scale={1.6}><EssenceTime seed={s} /></group>,
  // tablets (8.6) — glyph and tint per Prophecy
  proph_space_race:      (s) => <Tablet glyph="✦" tint={T.khaoHi} seed={s} />,
  proph_phantom_planet:  (s) => <Tablet glyph="◐" tint="#e8f4ff" seed={s} />,
  proph_dragonforce:     (s) => <Tablet glyph="♆" tint="#ff9b2e" seed={s} />,
  proph_time_to_pretend: (s) => <Tablet glyph="⧗" tint="#4ae8ff" seed={s} />,
  proph_tablet_omnis:    (s) => <Tablet glyph="Ω" tint={T.omnis} seed={s} />,
  proph_tablet_nullus:   (s) => <Tablet glyph="Ø" tint={T.nihil} seed={s} />,
  proph_ace_of_shades:   (s) => <Tablet glyph="♠" tint="#c9b8ff" seed={s} />,
  proph_bloody_hell:     (s) => <Tablet glyph="†" tint={T.blood} seed={s} />,
  // curios (8.6)
  curio_wizards_cap:   (s) => <WizardsCap seed={s} />,
  curio_circle_of_lyf: (s) => <CircleOfLyf seed={s} />,
  curio_jynxi_wares:   (s) => <JynxisWares seed={s} />,
  curio_spectacles:    (s) => <Spectacles seed={s} />,
  curio_mages_amulet:  (s) => <Necklace gem="#7c3aed" chain="#8a7ca8" seed={s} />,
  curio_elixir:        (s) => <Elixir seed={s} />,
  curio_warlocks_robe: (s) => <WarlocksRobe seed={s} />,
  curio_beb_buddy:     (s) => <BebBuddy seed={s} />,
  curio_badass_beb:    (s) => <BadassBeb seed={s} />,
  // ── the spells (8.7) — form per card, off its printed text ──
  // Nihil — wards
  tome_rays_of_hope:       (s) => <Ward form="rays" seed={s} />,
  tome_cleansing_burst:    (s) => <Ward form="disc" seed={s} />,
  tome_divine_justice:     (s) => <Ward form="scales" seed={s} />,
  tome_shower_of_lyt:      (s) => <Ward form="rain" seed={s} />,
  tome_radiant_aura:       (s) => <Ward form="rays" tint="#ffe6a0" seed={s} />,
  tome_phoenix_aura:       (s) => <Ward form="wings" tint="#ffb04a" seed={s} />,
  tome_wall_of_light:      (s) => <Ward form="wall" seed={s} />,
  tome_lyt_footed:         (s) => <Ward form="boots" seed={s} />,
  tome_sword_shield_nihil: (s) => <Ward form="blade" seed={s} />,
  // Omnia — omens
  tome_nothing_left:       (s) => <Omen form="blade" seed={s} />,
  tome_regnant:            (s) => <Omen form="crown" seed={s} />,
  tome_all_or_nothing:     (s) => <Omen form="scales" seed={s} />,
  tome_radiant_armor:      (s) => <Omen form="armor" seed={s} />,
  tome_omnis_corruption:   (s) => <Omen form="jet" seed={s} />,
  tome_superque:           (s) => <Omen form="star" seed={s} />,
  tome_wall_of_life:       (s) => <Omen form="wall" seed={s} />,
  tome_lyt_hearted:        (s) => <Omen form="blade" seed={s} />,
  tome_sword_shield_omnis: (s) => <Omen form="sword_shield" seed={s} />,
  // Ancient Sorcery — grimoires
  tome_not_here:             (s) => <Grimoire seed={s} />,
  tome_not_now:              (s) => <Grimoire tint="#a88cff" seed={s} />,
  tome_not_ever:             (s) => <Grimoire tint="#7c3aed" seed={s} />,
  tome_blood_in_blood_out:   (s) => <Grimoire tint={T.blood} blood seed={s} />,
  tome_blood_money:          (s) => <Grimoire tint={T.gold} blood seed={s} />,
  tome_river_of_blood:       (s) => <Grimoire tint={T.blood} blood seed={s} />,
  tome_shadow_blanket:       (s) => <Grimoire tint="#5b21b6" seed={s} />,
  tome_inverted_knife:       (s) => <Grimoire tint="#ff8aa0" seed={s} />,
  tome_light_casting_shadow: (s) => <Grimoire tint="#e8f4ff" seed={s} />,
  // Shadow Gambling — the table
  tome_temptation_manifested: (s) => <Gamble form="die" seed={s} />,
  tome_snake_eyes:            (s) => <Gamble form="dice" seed={s} />,
  tome_nevermore:             (s) => <Gamble form="die" seed={s} />,
  tome_underdogs_howl:        (s) => <Gamble form="cards" seed={s} />,
  tome_daft_fantasy:          (s) => <Gamble form="cards" seed={s} />,
  tome_goodbye_horses:        (s) => <Gamble form="cards" seed={s} />,
  tome_casino_royale:         (s) => <Gamble form="coin" seed={s} />,
  tome_dragon_deyes:          (s) => <Gamble form="dice" seed={s} />,
  tome_gambits_lucky_cat:     (s) => <Gamble form="cat" seed={s} />,
  // Contract — signed
  tome_devils_advocate:    (s) => <Contract form="mask" seed={s} />,
  tome_monkey_suite:       (s) => <Contract form="scroll" seed={s} />,
  tome_judge_jury:         (s) => <Contract form="gavel" seed={s} />,
  tome_restraining_ordeal: (s) => <Contract form="chains" seed={s} />,
  tome_agree_to_disagree:  (s) => <Contract form="two" seed={s} />,
  tome_solomon_godfry:     (s) => <Contract form="blade" seed={s} />,
  tome_magma_carta:        (s) => <Contract form="fire" seed={s} />,
  tome_seize_and_disease:  (s) => <Contract form="chains" seed={s} />,
  tome_king_of_the_castle: (s) => <Contract form="crown" seed={s} />,
};
export const PROP_IDS = Object.keys(PROPS);
export const hasProp = (cardId) => !!(cardId && PROPS[baseId(cardId)]);

// The prop standing on a card, at the card's centre. Unlike a being, a
// prop faces the VIEWER: a tablet's glyph, a house's windows and a
// tomb's gate are for reading, and a being fights the seam but a thing
// does not. Props are authored facing +z; on my seat that already looks
// at me, on the adversary's (π about Y) a half-turn does. `seed` fixes
// the idle phase per rift.
// SHIP 8.7 — THE CAST. `lifeKey` is the rift's key on the kick bus
// ("m:tome:1"); when the detector kicks it "cast" (a face-up placement
// landing, a set Tome revealed) the prop performs: lifts off the card,
// turns once, swells and settles. Polled per frame, one Map read, and
// composed with nothing else — a prop has no other motion of its own
// (its idle lives inside the prop, on the parts).
const CAST_MS = 760;
export function FieldProp({ cardId, seed = 0, stance = "mine", lifeKey = null }) {
  const g = useRef();
  const kT = useRef({ at: 0, t: 1 });
  useFrame((s, dt) => {
    const o = g.current;
    if (!o || !lifeKey) return;
    const kk = beingLife.kicks.get(lifeKey);
    const k = kT.current;
    if (kk && kk.at !== k.at) { k.at = kk.at; k.t = (Date.now() - kk.at) < 1500 ? 0 : 1; }
    if (k.t >= 1) { if (o.position.y !== 0) { o.position.y = 0; o.rotation.y = 0; o.scale.setScalar(1); } return; }
    k.t = Math.min(1, k.t + Math.min(dt || 0.016, 0.05) / ((CAST_MS / 1000) / motionRate(getMotion())));
    const b = Math.sin(k.t * Math.PI);
    o.position.y = b * 0.25;
    o.rotation.y = (1 - Math.pow(1 - k.t, 3)) * Math.PI * 2;
    o.scale.setScalar(1 + b * 0.22);
  });
  const make = cardId ? PROPS[baseId(cardId)] : null;
  if (!make) return null;
  return (
    <group rotation={[0, stance === "theirs" ? Math.PI : 0, 0]}>
      <group ref={g}>{make(seed)}</group>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE CELLAR — the Necropolis as a place (SHIP 8.5)
// ═══════════════════════════════════════════════════════════════════
// caP'n: "a small greenish vortex 'basement cellar door' type thing
// would be perfect." A stone frame set flush in the felt around the
// pile, two slab doors thrown open and leaning back on their hinges,
// and under the cards a slow green vortex — the way down. The pile
// stays the pile (top card, count, click). `dread` (0..1, the 2D
// board's own necropolis pulse) spins the vortex faster and burns it
// brighter as the pile grows.
function vortexTex() {
  return canvasTex("sf_cellar_vortex_v1", 256, 256, (g, w, h) => {
    g.fillStyle = "rgba(0,0,0,0)"; g.clearRect(0, 0, w, h);
    const cx = w / 2, cy = h / 2;
    const R = mulberry32(2024);
    // spiral arms of pale green on nothing — alpha carries the swirl
    for (let arm = 0; arm < 3; arm++) {
      for (let i = 0; i < 90; i++) {
        const t = i / 90;
        const a = arm * (Math.PI * 2 / 3) + t * Math.PI * 2.6;
        const r = 12 + t * 110;
        const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r;
        g.fillStyle = `rgba(${120 + R() * 60},${230},${150 + R() * 60},${0.55 * (1 - t) + 0.08})`;
        g.beginPath(); g.arc(x, y, 3 + (1 - t) * 6, 0, 7); g.fill();
      }
    }
    const core = g.createRadialGradient(cx, cy, 2, cx, cy, 40);
    core.addColorStop(0, "rgba(220,255,230,0.9)"); core.addColorStop(1, "rgba(60,200,120,0)");
    g.fillStyle = core; g.beginPath(); g.arc(cx, cy, 40, 0, 7); g.fill();
  });
}
export function CellarDoor({ position, dread = 0 }) {
  const swirl = useRef(); const glow = useRef(); const mist = useRef([]);
  const dRef = useRef(dread); dRef.current = dread;
  useFrame((s, dt) => {
    const t = s.clock.elapsedTime;
    const d = dRef.current;
    if (swirl.current) {
      swirl.current.rotation.z -= (0.5 + d * 1.6) * Math.min(dt || 0.016, 0.05);
      swirl.current.material.opacity = 0.55 + d * 0.35 + Math.sin(t * 1.3) * 0.08;
    }
    if (glow.current) glow.current.material.opacity = 0.10 + d * 0.25 + Math.sin(t * 0.9) * 0.04;
    mist.current.forEach((m, i) => {
      if (!m) return;
      const k = (t * 0.22 + i * 0.27) % 1;
      m.position.set(Math.sin(t * 0.7 + i * 2.1) * 0.35, 0.02 + k * 0.5, Math.cos(t * 0.5 + i * 1.3) * 0.45);
      m.material.opacity = (0.12 + d * 0.15) * Math.sin(k * Math.PI);
      m.scale.setScalar(0.5 + k * 1.2);
    });
  });
  const FW = CARD.W + 0.34, FD = CARD.H + 0.34;
  const slab = (sd) => (
    <group key={sd} position={[sd * (FW / 4), 0.02, FD / 2]} rotation={[-1.25, 0, 0]}>
      <mesh position={[0, 0, -FD / 4]} castShadow raycast={NO_RAY}>
        <boxGeometry args={[FW / 2 - 0.02, 0.035, FD / 2]} />
        <meshStandardMaterial map={stoneTex()} color="#5a4e66" {...STONE} />
      </mesh>
      <mesh position={[0, 0.03, -FD / 4 - 0.08]} rotation={[Math.PI / 2, 0, 0]} raycast={NO_RAY}>
        <torusGeometry args={[0.035, 0.008, 6, 12]} />
        <meshStandardMaterial map={metalTex()} color="#3a3642" {...METAL} />
      </mesh>
    </group>
  );
  return (
    <group position={[position[0], 0, position[2]]}>
      {/* the frame: four stone curbs flush with the felt */}
      {[[0, FD / 2 - 0.05, FW, 0.1], [0, -FD / 2 + 0.05, FW, 0.1], [FW / 2 - 0.05, 0, 0.1, FD], [-FW / 2 + 0.05, 0, 0.1, FD]].map(([x, z, w, dd], i) => (
        <mesh key={i} position={[x, 0.02, z]} castShadow receiveShadow raycast={NO_RAY}>
          <boxGeometry args={[w, 0.04, dd]} />
          <meshStandardMaterial map={stoneTex()} color="#4a4056" {...STONE} />
        </mesh>
      ))}
      {/* the way down: the vortex under the pile, and its light on the felt */}
      <mesh ref={swirl} position={[0, 0.004, 0]} rotation={[-Math.PI / 2, 0, 0]} raycast={NO_RAY}>
        <circleGeometry args={[Math.min(FW, FD) / 2 - 0.02, 32]} />
        <meshBasicMaterial map={vortexTex()} transparent opacity={0.6} depthWrite={false} toneMapped={false} />
      </mesh>
      <mesh ref={glow} position={[0, 0.003, 0]} rotation={[-Math.PI / 2, 0, 0]} raycast={NO_RAY}>
        <circleGeometry args={[Math.max(FW, FD) / 2 + 0.25, 32]} />
        <meshBasicMaterial color="#22c55e" transparent opacity={0.12} depthWrite={false} toneMapped={false} />
      </mesh>
      {/* the doors, thrown open at the back */}
      {slab(-1)}{slab(1)}
      {/* cellar breath */}
      {[0, 1, 2, 3].map((i) => (
        <mesh key={i} ref={(el) => (mist.current[i] = el)} raycast={NO_RAY}>
          <sphereGeometry args={[0.09, 8, 6]} />
          <meshBasicMaterial color="#6ee7a0" transparent opacity={0.1} depthWrite={false} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

// ── THE DEATH MARK — what the Necropolis says when a being is ruined ──
// caP'n: "when (let's say) a level 3 being is ruined, seeing something
// happen with Necropolis would be cool like a generic 'death' skull."
// A skull of pale green light rises out of the cellar, turns once to
// look at the table, and is gone. `level` (1–3) is the ruined card's
// own: a Beb's is small and quick; a Lord's is a third bigger, slower,
// and opens a green ring on the felt under it. World-space, one-shot.
const DEATH = { 1: { ms: 1500, size: 0.22, rise: 0.8, ring: 0 }, 2: { ms: 1900, size: 0.28, rise: 0.95, ring: 0.6 }, 3: { ms: 2400, size: 0.36, rise: 1.15, ring: 1.0 } };
export function DeathMark({ from, level = 1, onDone }) {
  const g = useRef(); const ring = useRef(); const mats = useRef([]);
  const born = useRef(0);   // set on the first frame — see GhostBody
  const spec = DEATH[Math.max(1, Math.min(3, level | 0))] || DEATH[1];
  const ms = spec.ms / motionRate(getMotion());
  const done = useRef(false);
  useFrame(() => {
    const o = g.current;
    if (!o || done.current) return;
    if (!born.current) born.current = Date.now();
    const k = Math.min(1, (Date.now() - born.current) / ms);
    const up = 1 - Math.pow(1 - k, 2);
    o.position.set(from[0], from[1] + 0.05 + up * spec.rise, from[2]);
    o.rotation.y = Math.PI + Math.sin(k * Math.PI) * 0.9;   // turns to face the table, then away
    const sc = spec.size * (k < 0.18 ? k / 0.18 : 1) * (1 + k * 0.25);
    o.scale.setScalar(Math.max(0.001, sc));
    const op = k < 0.15 ? k / 0.15 : Math.max(0, 1 - (k - 0.55) / 0.45);
    mats.current.forEach((m) => { if (m) m.opacity = op; });
    if (ring.current) {
      const rk = Math.min(1, k / 0.6);
      ring.current.scale.setScalar(0.2 + rk * spec.ring * 1.4 + 0.001);
      ring.current.material.opacity = spec.ring * (1 - rk) * 0.7;
    }
    if (k >= 1) { done.current = true; onDone && onDone(); }
  });
  const bone = (i, extra = {}) => (
    <meshBasicMaterial ref={(el) => (mats.current[i] = el)} color="#d8ffe6" transparent opacity={0} depthWrite={false} toneMapped={false} {...extra} />
  );
  return (
    <group>
      <group ref={g} position={from}>
        {/* cranium, brow, jaw — a skull is three masses and two holes */}
        <mesh raycast={NO_RAY}><sphereGeometry args={[1, 14, 12]} />{bone(0)}</mesh>
        <mesh position={[0, -0.55, 0.25]} scale={[0.72, 0.55, 0.7]} raycast={NO_RAY}><sphereGeometry args={[1, 12, 10]} />{bone(1)}</mesh>
        <mesh position={[0, -1.15, 0.35]} scale={[0.62, 0.32, 0.5]} raycast={NO_RAY}><sphereGeometry args={[1, 12, 10]} />{bone(2)}</mesh>
        {[-1, 1].map((sd) => (
          <mesh key={sd} position={[sd * 0.42, 0.05, 0.78]} scale={[1, 1.1, 0.6]} raycast={NO_RAY}>
            <sphereGeometry args={[0.3, 10, 8]} />
            {bone(sd < 0 ? 3 : 4, { color: "#052a14" })}
          </mesh>
        ))}
        <mesh position={[0, -0.45, 0.9]} rotation={[0.4, 0, 0]} scale={[0.6, 1, 0.6]} raycast={NO_RAY}>
          <coneGeometry args={[0.16, 0.3, 3]} />
          {bone(5, { color: "#052a14" })}
        </mesh>
        {/* the halo of its light */}
        <mesh scale={1.35} raycast={NO_RAY}>
          <sphereGeometry args={[1, 12, 10]} />
          {bone(6, { color: "#4ade80", side: THREE.BackSide })}
        </mesh>
      </group>
      <mesh ref={ring} position={[from[0], from[1] + 0.02, from[2]]} rotation={[-Math.PI / 2, 0, 0]} raycast={NO_RAY}>
        <ringGeometry args={[0.8, 1, 32]} />
        <meshBasicMaterial color="#4ade80" transparent opacity={0} depthWrite={false} toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE EXILE PIT — R112's rift, beside the Necropolis
// ═══════════════════════════════════════════════════════════════════
// A well of violet nothing: a stone lip, a void disc that breathes, a
// slow-turning ring of runes, and a thread of light rising out of it
// when something has been given up. `count` is player.exile.length. The
// click is the CALLER's — the parity gate lives in SeatSide.
export function ExilePit({ position, count = 0, onClick, onPointerOver, onPointerOut }) {
  const disc = useRef(); const ring = useRef(); const thread = useRef();
  const cRef = useRef(count); cRef.current = count;
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    const n = cRef.current;
    if (disc.current) disc.current.material.opacity = 0.75 + Math.sin(t * 0.9) * 0.1;
    if (ring.current) ring.current.rotation.z = -t * 0.18;
    if (thread.current) {
      thread.current.visible = n > 0;
      thread.current.scale.y = 0.6 + Math.min(1, n / 6) * 0.9 + Math.sin(t * 1.7) * 0.1;
      thread.current.material.opacity = 0.25 + Math.min(0.5, n * 0.08);
    }
  });
  const R = 0.46;
  return (
    <group position={[position[0], 0, position[2]]}>
      <mesh position={[0, 0.03, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow
        onClick={onClick} onPointerOver={onPointerOver} onPointerOut={onPointerOut}>
        <ringGeometry args={[R * 0.78, R + 0.1, 24]} />
        <meshStandardMaterial map={stoneTex()} color="#7a6690" {...STONE} emissive="#3a1f5e" emissiveIntensity={0.35} />
      </mesh>
      <mesh position={[0, 0.06, 0]} rotation={[Math.PI / 2, 0, 0]} raycast={NO_RAY}>
        <torusGeometry args={[R + 0.06, 0.035, 8, 28]} />
        <meshStandardMaterial map={stoneTex()} color="#5a4870" {...STONE} emissive="#2a1444" emissiveIntensity={0.4} />
      </mesh>
      {/* the void itself takes the click too — a pit whose rim is the
          only button has a dead centre exactly where a player aims */}
      <mesh ref={disc} position={[0, 0.032, 0]} rotation={[-Math.PI / 2, 0, 0]}
        onClick={onClick} onPointerOver={onPointerOver} onPointerOut={onPointerOut}>
        <circleGeometry args={[R * 0.8, 24]} />
        <meshBasicMaterial color="#12061f" transparent opacity={0.8} toneMapped={false} />
      </mesh>
      <mesh ref={ring} position={[0, 0.04, 0]} rotation={[-Math.PI / 2, 0, 0]} raycast={NO_RAY}>
        <ringGeometry args={[R * 0.55, R * 0.62, 7]} />
        <meshBasicMaterial color="#a78bfa" transparent opacity={0.9} toneMapped={false} side={THREE.DoubleSide} depthWrite={false} />
      </mesh>
      <mesh ref={thread} position={[0, 0.45, 0]} visible={false} raycast={NO_RAY}>
        <cylinderGeometry args={[0.02, 0.06, 0.8, 8, 1, true]} />
        <meshBasicMaterial color="#8b6bd6" transparent opacity={0.3} depthWrite={false} toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}
